import { IDetailsRowProps, IDropdownOption } from '@fluentui/react';
import {
  Button,
  buttonStylesDanger,
  buttonStylesPrimary,
  CommonStyles,
  detailsHeaderStylesBasic,
  DetailsListEditor,
  detailsListStylesBasic,
  FieldType,
  highlightRowBorder,
  IColumnWithDataType,
  IDetailsListEditorProps,
  IEditorProps,
  ListRow,
  listRowStylesThinWithCellDivider,
  onRenderRequiredColumnHeader,
  useClassNames,
} from '@h2oai/ui-kit';
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';

import { FeatureSchemaDraftWithDepth } from '../../featurestore/models';
import {
  editFeatureMapListStylesDefault,
  IEditFeatureMapListClassNames,
  IEditFeatureMapListStyles,
} from './EditFeatureMapList.styles';

interface FeatureNameValueMap {
  name?: string;
  value?: string;
}

const getColumns = (onDelete: (e: React.MouseEvent<any>, item: FeatureNameValueMap) => void) => {
  const columns: IColumnWithDataType[] = [
    {
      key: 'name',
      name: 'Feature Name',
      fieldName: 'name',
      minWidth: 250,
      data: {
        listCellProps: { editable: true },
        editorProps: {
          fieldType: FieldType.dropdown,
          fieldProps: {
            placeholder: 'Select Feature',
          },
        },
      },
      onRenderHeader: onRenderRequiredColumnHeader,
    },
    {
      key: 'value',
      name: 'Value',
      fieldName: 'value',
      minWidth: 150,
      data: {
        listCellProps: { editable: true },
      },
    },
    {
      key: 'buttons',
      name: 'Actions',
      minWidth: 231,
      maxWidth: 231,
      onRender: (item) => {
        return (
          <div
            style={{
              ...(CommonStyles.flexCenterVertically as CSSProperties),
              width: '100%',
              textAlign: 'right',
            }}
          >
            <div>
              <Button text="Delete" title="Delete" styles={buttonStylesDanger} onClick={(e) => onDelete(e, item)} />
            </div>
          </div>
        );
      },
    },
  ];
  return columns;
};

const getFeatureNameOptions = (
  features: FeatureSchemaDraftWithDepth[] = [],
  items: FeatureNameValueMap[]
): { options: IDropdownOption[]; newItems: FeatureNameValueMap[] } => {
  const nameSet = new Set<string>();
  const itemNameSet = new Set<string>();
  let options: IDropdownOption[] = [];
  features.forEach(({ name }) => {
    if (name && !nameSet.has(name)) {
      nameSet.add(name);
      options.push({ key: name, text: name });
    }
  });
  // remove the items are not in Features.
  const newItems = items.filter(({ name = '' }) => {
    if (nameSet.has(name)) {
      itemNameSet.add(name);
      return true;
    }
    return false;
  });
  // remove options are in the items
  options = options.filter(({ text }) => !itemNameSet.has(text));
  return { newItems, options };
};

const getRowIndexByCellEl = (el: HTMLElement) => {
  const cellEl = el.closest('.h2o-ListCell-root') as HTMLDivElement;
  return +(cellEl.dataset.rowIndex || 0);
};

const getFeatureMapping = (items: FeatureNameValueMap[]) => {
  const map: { [key: string]: string } = {};
  items.forEach(({ name, value }) => {
    if (name && value) {
      map[name] = value;
    }
  });
  return map;
};

const getItems = (map?: { [key: string]: string }) =>
  map ? Object.keys(map).map((key) => ({ name: key, value: map[key] })) : [];

interface IEditFeatureMapListProps {
  map?: { [key: string]: string };
  features: FeatureSchemaDraftWithDepth[];
  onUpdate: (featureMapping: { [key: string]: string }) => void;
}

export const EditFeatureMapList = ({ map, features, onUpdate }: IEditFeatureMapListProps) => {
  const [optionalEditorFieldProps, setOptionalEditorFieldProps] = useState<{ [key: string]: any }>();
  const refFeatureNameOptions = useRef<IDropdownOption[]>([]);
  const [items, setItems] = useState<FeatureNameValueMap[]>(getItems(map));
  const onEndEdit = useCallback(
    (changed?: boolean, value?: any, rowIndex?: number, fieldName?: string) => {
      if (changed && rowIndex !== undefined && fieldName) {
        (items[rowIndex] as any)[fieldName] = value;
        setItems([...items]);
        if (fieldName === 'name') {
          refFeatureNameOptions.current = refFeatureNameOptions.current.filter((option) => option.key !== value);
        }
        onUpdate(getFeatureMapping(items));
      }
    },
    [items]
  );
  const onAdd = useCallback(() => {
    const features = [...items, { name: '', value: '' }];
    setItems(features);
    onUpdate(getFeatureMapping(items));
  }, [items]);
  const onDelete = useCallback(
    (e: React.MouseEvent) => {
      const rowIndex = getRowIndexByCellEl(e.target as HTMLElement);
      const { name } = items[rowIndex];
      if (name) {
        const options = refFeatureNameOptions.current;
        options.push({ key: name, text: name });
        refFeatureNameOptions.current = options;
      }
      items.splice(rowIndex, 1);
      setItems([...items]);
      onUpdate(getFeatureMapping(items));
    },
    [items]
  );
  const onBeforeStartEdit = useCallback((editorProps: IEditorProps) => {
    const { fieldName } = editorProps.meta || {};
    if (fieldName === 'name') {
      setOptionalEditorFieldProps({
        options: refFeatureNameOptions.current,
      });
    }
  }, []);
  const featureListProps: IDetailsListEditorProps = {
    columns: getColumns(onDelete),
    headerStyles: detailsHeaderStylesBasic,
    styles: detailsListStylesBasic,
    items,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [], itemIndex } = props || {};
      return (
        <ListRow
          styles={listRowStylesThinWithCellDivider}
          className={!item.name ? 'is-empty-row' : undefined}
          hideHeader
          data={item}
          columns={columns}
          itemIndex={itemIndex}
          onMouseEnter={(e) => highlightRowBorder(e)}
          onMouseLeave={(e) => highlightRowBorder(e, false)}
        />
      );
    },
    onBeforeStartEdit,
    onEndEdit,
    optionalEditorFieldProps,
  };
  useEffect(() => {
    // when it gets new features, some features may be removed.
    const { options, newItems } = getFeatureNameOptions(features, items);
    refFeatureNameOptions.current = options;
    setItems(newItems);
    if (map) {
      setItems(getItems(map));
    }
  }, [features, map]);
  const classNames = useClassNames<IEditFeatureMapListStyles, IEditFeatureMapListClassNames>(
    'EditFeatureMapList',
    editFeatureMapListStylesDefault
  );
  return (
    <div className={classNames.root} data-test="edit-feature-map-list">
      <div className={classNames.subTitle}>
        <Button styles={buttonStylesPrimary} iconName="Add" text="Add Default Value For Feature" onClick={onAdd} />
      </div>
      <div className={classNames.list}>
        <DetailsListEditor {...featureListProps} />
      </div>
    </div>
  );
};
